// BOARD-ORDER: the board and space arrangement writes, optimistic (api/optimistic.ts): a drag,
// "Move up", a colour or a lead shows at once in the spaces tree, Work home and the board
// header, and undoes itself when the server refuses. The cache write follows the server's rule
// exactly (board-order.ts), and the server's answer is written over it.
import type { BoardColor } from "@companyos/ui";
import { toast } from "@companyos/ui";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { useOptimisticMutation } from "../../api/optimistic";
import type { CompanyAccess } from "../company";
import { moveBoardIn, moveSpaceIn } from "./board-order";
import { type BoardSummary, workKeys } from "./work-queries";

type Board = Awaited<ReturnType<Api["client"]["boards"]["get"]>>;
type Space = Awaited<ReturnType<Api["client"]["spaces"]["get"]>>;
type Page<T> = { data: T[]; next_cursor: string | null; prev_cursor: string | null };

/** Rewrites the cached boards list, every summary and the open board's own copy. */
function patchBoards(
  qc: QueryClient,
  co: string,
  boards: (list: Board[]) => Board[],
  summary?: (s: BoardSummary, board: Board | undefined) => BoardSummary,
) {
  let next: Board[] | undefined;
  qc.setQueryData<Page<Board>>(workKeys.boards(co), (p) => {
    if (!p) return p;
    next = boards(p.data);
    return { ...p, data: next };
  });
  const byId = new Map((next ?? []).map((b) => [b.id, b] as const));
  if (summary)
    qc.setQueryData<BoardSummary[]>(workKeys.summaries(co), (rows) =>
      rows?.map((s) => summary(s, byId.get(s.board_id))),
    );
  for (const b of next ?? [])
    qc.setQueryData<Board>(workKeys.board(co, b.id), (old) => (old ? { ...old, ...b } : old));
}

/** The summary row as the board now reads (space, order, colour; the lead when it names one). */
const summaryFrom = (s: BoardSummary, b: Board | undefined): BoardSummary =>
  b
    ? {
        ...s,
        space_id: b.space_id,
        position: b.position,
        color: b.color,
        lead_seat_id: b.lead_seat_id ?? s.lead_seat_id,
      }
    : s;

export function useBoardWrites(api: Api, company: CompanyAccess) {
  const qc = useQueryClient();
  const co = company.id;
  const settle = () => [workKeys.boards(co), ["company", co, "work", "space"] as const];
  const current = (id: string) =>
    qc.getQueryData<Page<Board>>(workKeys.boards(co))?.data.find((b) => b.id === id) ??
    qc.getQueryData<Board>(workKeys.board(co, id));
  const reconcile = (b: Board) =>
    patchBoards(qc, co, (list) => list.map((x) => (x.id === b.id ? b : x)), summaryFrom);

  const move = useOptimisticMutation({
    keys: (v) => [workKeys.boards(co), workKeys.board(co, v.board.id)],
    apply: (v: { board: Board; spaceId: string; beforeId: string | null }) =>
      patchBoards(
        qc,
        co,
        (list) => moveBoardIn(list, v.board.id, v.spaceId, v.beforeId),
        summaryFrom,
      ),
    mutationFn: (v) =>
      api.client.boards.move({
        params: { co, id: v.board.id },
        body: { space_id: v.spaceId, before_board_id: v.beforeId },
      }),
    reconcile,
    settle,
  });

  const patch = useOptimisticMutation({
    keys: (v) => [workKeys.boards(co), workKeys.board(co, v.id)],
    apply: (v: {
      id: string;
      body: {
        color?: BoardColor | null;
        lead_seat_id?: string | null;
        settings?: Board["settings"];
      };
    }) =>
      patchBoards(
        qc,
        co,
        (list) => list.map((b) => (b.id === v.id ? { ...b, ...v.body } : b)),
        (s, b) =>
          b && s.board_id === v.id
            ? {
                ...s,
                ...(v.body.color !== undefined ? { color: v.body.color } : {}),
                ...(v.body.lead_seat_id !== undefined ? { lead_seat_id: v.body.lead_seat_id } : {}),
              }
            : s,
      ),
    mutationFn: (v) => {
      const b = current(v.id);
      return api.client.boards.update({
        params: { co, id: v.id },
        ifMatch: b?.version ?? 1,
        body: v.body,
      });
    },
    reconcile,
    settle,
  });

  const moveSpace = useOptimisticMutation({
    keys: () => [workKeys.spaces(co)],
    apply: (v: { space: Space; beforeId: string | null }) =>
      qc.setQueryData<Page<Space>>(workKeys.spaces(co), (p) =>
        p ? { ...p, data: moveSpaceIn(p.data, v.space.id, v.beforeId) } : p,
      ),
    mutationFn: (v) =>
      api.client.spaces.move({
        params: { co, id: v.space.id },
        body: { before_space_id: v.beforeId },
      }),
    settle: () => [workKeys.spaces(co), ["company", co, "chat"] as const],
  });

  return {
    /** Puts `board` before `beforeId` (null: last) in `spaceId`; says so when the space changes. */
    move: (board: Board, spaceId: string, beforeId: string | null, spaceName?: string) =>
      move.mutate(
        { board, spaceId, beforeId },
        spaceId !== board.space_id && spaceName
          ? {
              onSuccess: () =>
                toast.add({ title: `${board.name} moved to ${spaceName}`, type: "success" }),
            }
          : undefined,
      ),
    color: (board: Pick<Board, "id">, color: BoardColor | null) =>
      patch.mutate({ id: board.id, body: { color } }),
    lead: (board: Pick<Board, "id">, seatId: string | null) =>
      patch.mutate({ id: board.id, body: { lead_seat_id: seatId } }),
    /** WORKFORCE-BOARDVIEW: the tab the board opens on for everyone (null: its default view). */
    defaultKind: (
      board: Pick<Board, "id" | "settings">,
      kind: Board["settings"]["default_kind"],
      on?: { onSuccess?: () => void; onError?: (error: unknown) => void },
    ) =>
      patch.mutate(
        { id: board.id, body: { settings: { ...board.settings, default_kind: kind } } },
        on
          ? {
              ...(on.onSuccess ? { onSuccess: () => on.onSuccess?.() } : {}),
              ...(on.onError ? { onError: (e: unknown) => on.onError?.(e) } : {}),
            }
          : undefined,
      ),
    moveSpace: (space: Space, beforeId: string | null) => moveSpace.mutate({ space, beforeId }),
  };
}
